import { useEffect, useMemo, useState } from "react";
import {
  createRemoteOrganization,
  inviteRemoteOrganizationMember,
  listRemoteOrganizationMembers,
  updateRemoteOrganization,
  updateRemoteOrganizationMember,
} from "../api";
import { Icon } from "../icons";
import type {
  AuthSession,
  OrganizationMember,
  OrganizationRole,
  UserContext,
} from "../types";

const ROLES: OrganizationRole[] = [
  "Administrador",
  "Gerencia",
  "Operaciones",
  "Supervisor",
  "Tecnico",
  "Comercial",
  "Auditor",
];

const ROLE_DESCRIPTION: Record<OrganizationRole, string> = {
  Administrador: "Configura la organización, invita usuarios y administra canales.",
  Gerencia: "Accede a toda la operación y sus reportes, sin administrar usuarios.",
  Operaciones: "Gestiona conversaciones, compromisos, visitas y seguimiento diario.",
  Supervisor: "Coordina casos y visitas, y actualiza el estado operacional.",
  Tecnico: "Trabaja sobre visitas, diagnósticos y conversaciones asignadas.",
  Comercial: "Gestiona oportunidades, respuestas, cotizaciones y seguimientos.",
  Auditor: "Consulta información y evidencia sin modificar la operación.",
};

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;
type MemberFilter = "all" | OrganizationMember["status"];

export function OrganizationsView({
  context,
  session,
  onSwitch,
  onRefresh,
  notify,
}: {
  context: UserContext;
  session: AuthSession;
  onSwitch: (organizationId: string) => Promise<void>;
  onRefresh: (preferredOrganizationId?: string) => Promise<void>;
  notify: Notify;
}) {
  const active = context.organizations.find((item) => item.id === context.activeOrganizationId);
  const activeCount = context.organizations.filter((item) => item.status === "active").length;
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [editName, setEditName] = useState(active?.name ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => setEditName(active?.name ?? ""), [active?.id, active?.name]);

  async function createOrganization() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const created = await createRemoteOrganization(
        { name: name.trim(), slug: slug.trim() || undefined },
        session,
      );
      setName("");
      setSlug("");
      await onRefresh(created.id);
      await onSwitch(created.id);
      notify("success", "Organización creada con un espacio de datos independiente.");
    } catch (cause) {
      notify("danger", "No se pudo crear la organización: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function renameOrganization() {
    if (!active || !editName.trim()) return;
    setBusy(true);
    try {
      await updateRemoteOrganization(active.id, { name: editName.trim() }, session);
      await onRefresh(active.id);
      notify("success", "Nombre de la organización actualizado.");
    } catch (cause) {
      notify("danger", "No se pudo editar la organización: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="standard-page admin-page">
      <section className="standard-page-header compact-page-header">
        <div>
          <p className="section-kicker">ADMINISTRACIÓN MULTIEMPRESA</p>
          <h1>Organizaciones</h1>
          <p>Cada empresa funciona como un espacio independiente, con sus propios usuarios, permisos, conversaciones y canales.</p>
        </div>
      </section>

      <section className="admin-overview-grid" aria-label="Resumen de organizaciones">
        <article className="admin-overview-card">
          <span><Icon name="settings" size={19} /></span>
          <div><strong>{active?.name ?? "Sin organización activa"}</strong><small>Espacio de trabajo actual</small></div>
        </article>
        <article className="admin-overview-card">
          <span><Icon name="team" size={19} /></span>
          <div><strong>{active?.role ?? "Sin rol"}</strong><small>Tu permiso en este espacio</small></div>
        </article>
        <article className="admin-overview-card">
          <span><Icon name="check" size={19} /></span>
          <div><strong>{activeCount} de {context.organizations.length}</strong><small>Organizaciones activas disponibles</small></div>
        </article>
      </section>

      <div className="admin-two-column">
        <section className="card-panel admin-panel">
          <div className="panel-title-row">
            <div><p className="section-kicker">TUS ESPACIOS</p><h2>Organizaciones disponibles</h2></div>
            <span className="count-badge">{context.organizations.length}</span>
          </div>
          <div className="organization-list">
            {context.organizations.map((organization) => (
              <article className={organization.id === active?.id ? "organization-row active" : "organization-row"} key={organization.id}>
                <span className="organization-avatar">{initials(organization.name)}</span>
                <div>
                  <span className="organization-row-meta">
                    <strong>{organization.name}</strong>
                    {organization.id === active?.id ? <span className="current-user-badge">En uso</span> : null}
                  </span>
                  <small>{organization.slug} · {organization.role}</small>
                </div>
                <span className={"state-chip " + organization.status}>{organization.status === "active" ? "Activa" : organization.status === "invited" ? "Invitación" : "Suspendida"}</span>
                <button className="button soft compact" disabled={organization.id === active?.id || organization.status === "suspended"} onClick={() => void onSwitch(organization.id)} type="button">
                  {organization.id === active?.id ? "Actual" : "Abrir"}
                </button>
              </article>
            ))}
          </div>
          <div className="admin-inline-note">
            <Icon name="check" size={16} />
            <span>El navegador solicita una organización activa, pero la API comprueba siempre la membresía antes de entregar o modificar datos.</span>
          </div>
        </section>

        <div className="admin-stack">
          {active?.role === "Administrador" ? (
            <section className="card-panel admin-panel">
              <div className="panel-title-row"><div><p className="section-kicker">ORGANIZACIÓN ACTIVA</p><h2>Editar nombre</h2></div></div>
              <label className="field-label"><span>Nombre visible</span><input value={editName} onChange={(event) => setEditName(event.target.value)} /></label>
              <button className="button primary" disabled={busy || !editName.trim() || editName.trim() === active.name} onClick={() => void renameOrganization()} type="button">
                Guardar cambios
              </button>
            </section>
          ) : (
            <section className="card-panel admin-panel">
              <div className="panel-title-row"><div><p className="section-kicker">TU ACCESO</p><h2>{active?.role ?? "Sin rol"}</h2></div></div>
              <p className="member-role-copy">{active ? ROLE_DESCRIPTION[active.role] : "No tienes una organización activa."}</p>
            </section>
          )}

          <section className="card-panel admin-panel">
            <div className="panel-title-row"><div><p className="section-kicker">NUEVO ESPACIO</p><h2>Crear organización</h2></div></div>
            <div className="form-stack">
              <label className="field-label"><span>Nombre</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ej. Pocatinta SPA" /></label>
              <label className="field-label"><span>Identificador opcional</span><input value={slug} onChange={(event) => setSlug(event.target.value)} placeholder="pocatinta-spa" /><small>Clave estable usada para separar datos y permisos.</small></label>
            </div>
            <button className="button primary" disabled={busy || !name.trim()} onClick={() => void createOrganization()} type="button">
              <Icon name="plus" size={16} /> Crear organización
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

export function UsersView({
  organizationId,
  organizationName,
  session,
  notify,
}: {
  organizationId: string;
  organizationName: string;
  session: AuthSession;
  notify: Notify;
}) {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrganizationRole>("Operaciones");
  const [filter, setFilter] = useState<MemberFilter>("all");
  const [busy, setBusy] = useState(false);
  const currentUserId = contextUserId(session);

  useEffect(() => {
    let active = true;
    void listRemoteOrganizationMembers(organizationId, session)
      .then((items) => active && setMembers(items))
      .catch((cause) => active && notify("danger", "No se pudieron cargar los usuarios: " + errorMessage(cause)));
    return () => { active = false; };
    // notify is intentionally omitted: it is recreated by the app shell and
    // must not make this data-loading effect run after every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, session]);

  const counts = useMemo(() => ({
    total: members.length,
    active: members.filter((item) => item.status === "active").length,
    invited: members.filter((item) => item.status === "invited").length,
    suspended: members.filter((item) => item.status === "suspended").length,
    activeAdmins: members.filter((item) => item.status === "active" && item.role === "Administrador").length,
  }), [members]);

  const visibleMembers = useMemo(
    () => filter === "all" ? members : members.filter((item) => item.status === filter),
    [filter, members],
  );

  async function reloadMembers() {
    setMembers(await listRemoteOrganizationMembers(organizationId, session));
  }

  async function invite() {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await inviteRemoteOrganizationMember(organizationId, { email: email.trim(), role }, session);
      await reloadMembers();
      setEmail("");
      setFilter("all");
      notify("success", "Usuario invitado y vinculado únicamente a " + organizationName + ".");
    } catch (cause) {
      notify("danger", "No se pudo invitar al usuario: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function changeMember(member: OrganizationMember, changes: { role?: OrganizationRole; status?: "active" | "suspended" }) {
    const suspending = changes.status === "suspended";
    if (suspending && !window.confirm(`¿Suspender el acceso de ${member.email} a ${organizationName}?`)) return;
    setBusy(true);
    try {
      await updateRemoteOrganizationMember(organizationId, member.userId, changes, session);
      await reloadMembers();
      notify("success", "Membresía actualizada.");
    } catch (cause) {
      notify("danger", "No se pudo actualizar la membresía: " + errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="standard-page admin-page">
      <section className="standard-page-header compact-page-header">
        <div>
          <p className="section-kicker">USUARIOS · {organizationName.toUpperCase()}</p>
          <h1>Equipo y permisos</h1>
          <p>Una persona puede participar en varias organizaciones y tener un rol distinto en cada una. Los permisos nunca se comparten automáticamente.</p>
        </div>
      </section>

      <section className="member-summary" aria-label="Resumen de usuarios">
        <article><strong>{counts.total}</strong><span>Total</span></article>
        <article><strong>{counts.active}</strong><span>Activos</span></article>
        <article><strong>{counts.invited}</strong><span>Invitados</span></article>
        <article><strong>{counts.activeAdmins}</strong><span>Administradores</span></article>
      </section>

      <section className="card-panel invite-panel">
        <div><p className="section-kicker">INVITAR</p><h2>Agregar usuario</h2><p className="invite-role-description">La invitación crea una membresía sólo para esta organización.</p></div>
        <label className="field-label grow"><span>Correo corporativo</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="persona@empresa.cl" /></label>
        <label className="field-label"><span>Rol inicial</span><select value={role} onChange={(event) => setRole(event.target.value as OrganizationRole)}>{ROLES.map((item) => <option key={item}>{item}</option>)}</select><small>{ROLE_DESCRIPTION[role]}</small></label>
        <button className="button primary" disabled={busy || !email.trim()} onClick={() => void invite()} type="button"><Icon name="send" size={16} /> Enviar invitación</button>
      </section>

      <section className="card-panel admin-panel member-panel">
        <div className="panel-title-row"><div><p className="section-kicker">EQUIPO</p><h2>Miembros de {organizationName}</h2></div><span className="count-badge">{visibleMembers.length}</span></div>

        <div className="member-toolbar">
          <div className="member-toolbar-copy"><strong>Accesos por organización</strong><small>Cambiar un rol aquí no afecta otros espacios del usuario.</small></div>
          <div className="member-filter" role="group" aria-label="Filtrar usuarios">
            {(["all", "active", "invited", "suspended"] as MemberFilter[]).map((value) => (
              <button className={filter === value ? "active" : ""} key={value} onClick={() => setFilter(value)} type="button">
                {value === "all" ? "Todos" : value === "active" ? "Activos" : value === "invited" ? "Invitados" : "Suspendidos"}
              </button>
            ))}
          </div>
        </div>

        <div className="member-table">
          {visibleMembers.map((member) => {
            const isCurrentUser = member.userId === currentUserId;
            const isProtectedLastAdmin = member.role === "Administrador" && member.status === "active" && counts.activeAdmins <= 1;
            return (
              <article className="member-row" key={member.userId}>
                <span className="mini-avatar">{initials(member.email)}</span>
                <div>
                  <span className="member-primary-line"><strong>{member.email}</strong>{isCurrentUser ? <span className="current-user-badge">Tú</span> : null}</span>
                  <small>{member.status === "invited" ? "Invitación pendiente" : member.status === "suspended" ? "Acceso suspendido" : "Acceso activo"}</small>
                  <span className="member-role-copy">{ROLE_DESCRIPTION[member.role]}</span>
                </div>
                <select
                  aria-label={`Rol de ${member.email}`}
                  disabled={busy || isProtectedLastAdmin}
                  title={isProtectedLastAdmin ? "Debe existir al menos un Administrador activo" : undefined}
                  value={member.role}
                  onChange={(event) => void changeMember(member, { role: event.target.value as OrganizationRole })}
                >
                  {ROLES.map((item) => <option key={item}>{item}</option>)}
                </select>
                <button
                  className="button soft compact"
                  disabled={busy || isCurrentUser || isProtectedLastAdmin}
                  title={isCurrentUser ? "No puedes suspender tu propia sesión" : isProtectedLastAdmin ? "Debe existir al menos un Administrador activo" : undefined}
                  onClick={() => void changeMember(member, { status: member.status === "suspended" ? "active" : "suspended" })}
                  type="button"
                >
                  {member.status === "suspended" ? "Reactivar" : "Suspender"}
                </button>
              </article>
            );
          })}
          {!visibleMembers.length ? <div className="friendly-empty member-empty"><span><Icon name="team" size={24} /></span><strong>Sin usuarios en este filtro</strong><p>Cambia el filtro o invita a una persona para comenzar.</p></div> : null}
        </div>

        <div className="role-guide">
          <strong>Guía rápida de roles</strong>
          <div className="role-guide-grid">
            {ROLES.map((item) => <article className="role-guide-card" key={item}><strong>{item}</strong><span>{ROLE_DESCRIPTION[item]}</span></article>)}
          </div>
        </div>
      </section>
    </div>
  );
}

function contextUserId(session: AuthSession) {
  try {
    const payload = session.idToken.split(".")[1] ?? "";
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return String(JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="))).sub ?? "");
  } catch {
    return "";
  }
}

function initials(value: string) {
  return value.split(/[@.\s_-]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "CS";
}

function errorMessage(cause: unknown) {
  const code = cause instanceof Error ? cause.message : "ERROR_DESCONOCIDO";
  const labels: Record<string, string> = {
    LAST_ACTIVE_ADMIN_REQUIRED: "debe mantenerse al menos un Administrador activo",
    CANNOT_SUSPEND_CURRENT_ADMIN: "no puedes suspender tu propio acceso",
    ORGANIZATION_ACCESS_FORBIDDEN: "no tienes acceso a esa organización",
    MEMBERSHIP_NOT_FOUND: "la membresía ya no existe",
  };
  return labels[code] ?? code.replaceAll("_", " ").toLocaleLowerCase("es-CL");
}
